import { useEffect, useState } from "react";

import { listOperatorsIncludingArchived } from "../../../../modules/integrations/accounts";
import { reportError } from "@/v1/sentry";
import { ChangeLog } from "../../../api/graphql";
import { OperatorNames } from "./helpers";

/*
 * Change-log fields whose value is an operator id. Kept next to the check so
 * the fetch is skipped for the many logs that hold no operator id at all.
 */
const OPERATOR_ID_FIELDS = ["assignee"];

/**
 * Whether any of these logs touches a field holding an operator id. Used to
 * avoid an accounts request for the majority of Action logs, which never
 * mention an operator.
 */
export const hasOperatorIdField = (changeLogs?: ChangeLog[]): boolean =>
  (changeLogs ?? []).some(({ changes }) =>
    Object.keys(changes ?? {}).some((key) =>
      OPERATOR_ID_FIELDS.includes(key.replace(/__added$|__deleted$/, ""))
    )
  );

/**
 * Operator id to display name, for rendering operator ids in the Action log.
 *
 * Includes archived operators on purpose: an assignment can point at someone
 * who has since left, and those entries are exactly why a reader opens the log.
 *
 * Best-effort. A failure is reported and resolves to an empty map, which makes
 * the log fall back to raw ids rather than failing the drawer: an unreadable
 * log still beats no log.
 */
export const useOperatorNames = (enabled: boolean): OperatorNames => {
  const [operatorNames, setOperatorNames] = useState<OperatorNames>({});

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    listOperatorsIncludingArchived()
      .then((operators) => {
        if (cancelled) {
          return;
        }
        setOperatorNames(
          Object.fromEntries(operators.map((op) => [op.id, op.displayName]))
        );
      })
      .catch((error) => {
        reportError(error as Error);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return operatorNames;
};
